// src/pages/suppliers/ProductRegistration.jsx

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Paper,
  Grid,
  Stepper,
  Step,
  StepLabel,
  Avatar,
  Chip,
  Divider,
  Alert,
  Snackbar,
  Slide,
  InputAdornment,
  FormHelperText,
  LinearProgress,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useMediaQuery,
  useTheme,
  alpha,
  Container,
  StepConnector,
  stepConnectorClasses,
  styled,
  Popover,
  Menu,
  IconButton,
  Badge,
  Drawer,
  Autocomplete,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Collapse,
  Card,
  CardContent,
  CardHeader,
  Fade,
  Grow,
  Zoom,
} from '@mui/material';

// ============================================
// ICON IMPORTS
// ============================================
import {
  AttachMoney as MoneyIcon,
  LockOutlined as LockOutlinedIcon,
  Search as SearchIcon,
  AddPhotoAlternate as AddPhotoIcon,
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  Description as DescriptionIcon,
  LocalShipping as LocalShippingIcon,
  Security as SecurityIcon,
  Image as ImageIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  Menu as MenuIcon,
  Notifications as NotificationsIcon,
  Person as PersonIcon,
  Logout as LogoutIcon,
  Settings as SettingsIcon,
  Science as ScienceIcon,
  History as HistoryIcon,
  ContentCopy as ContentCopyIcon,
  Dashboard as DashboardIcon,
  Inventory as InventoryIcon,
  ShoppingCart as ShoppingCartIcon,
  Analytics as AnalyticsIcon,
  Verified as VerifiedIcon,
  TrendingUp as TrendingUpIcon,
  Eco as EcoIcon,
  Shield as ShieldIcon,
  Public as PublicIcon,
} from '@mui/icons-material';

// Import shared components
import Preloader from '../../theme/Preloader';
import Sidebar from './components/Sidebar';
import Header from './components/Header';
import Footer from './components/Footer';
import { BRAND_COLORS } from '../buyers/components/styles';
import { formatDate } from '../buyers/components/utils';

// ============================================
// ENHANCED STYLES
// ============================================

// Custom Step Connector
const CustomStepConnector = styled(StepConnector)(({ theme }) => ({
  [`&.${stepConnectorClasses.alternativeLabel}`]: {
    top: 10,
    left: 'calc(-50% + 16px)',
    right: 'calc(50% + 16px)',
  },
  [`& .${stepConnectorClasses.line}`]: {
    borderColor: '#E5E7EB',
    borderWidth: 2,
    borderRadius: 1,
    transition: 'border-color 0.3s ease',
  },
  [`&.${stepConnectorClasses.active}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      borderColor: BRAND_COLORS.primary,
      borderWidth: 2,
      background: `linear-gradient(90deg, ${BRAND_COLORS.primary}, ${BRAND_COLORS.primaryLight})`,
    },
  },
  [`&.${stepConnectorClasses.completed}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      borderColor: BRAND_COLORS.success,
      borderWidth: 2,
    },
  },
}));

// Custom Step Icon
const CustomStepIcon = React.memo(({ active, completed, icon }) => {
  const getIcon = useCallback(() => {
    if (completed) {
      return <CheckIcon sx={{ color: '#FFFFFF', fontSize: 20 }} />;
    }
    switch (icon) {
      case 1:
        return <DescriptionIcon sx={{ fontSize: 18 }} />;
      case 2:
        return <LocalShippingIcon sx={{ fontSize: 18 }} />;
      case 3:
        return <ShieldIcon sx={{ fontSize: 18 }} />;
      default:
        return icon;
    }
  }, [completed, icon]);

  return (
    <Box
      sx={{
        width: 38,
        height: 38,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: active 
          ? BRAND_COLORS.primary 
          : (completed ? BRAND_COLORS.success : '#E5E7EB'),
        color: active || completed ? '#FFFFFF' : '#9CA3AF',
        transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
        boxShadow: active 
          ? `0 4px 20px ${alpha(BRAND_COLORS.primary, 0.4)}` 
          : (completed ? `0 4px 20px ${alpha(BRAND_COLORS.success, 0.3)}` : 'none'),
        transform: active ? 'scale(1.1)' : 'scale(1)',
        '& .MuiSvgIcon-root': {
          fontSize: 18,
        },
        '&:hover': {
          transform: 'scale(1.05)',
        },
      }}
    >
      {getIcon()}
    </Box>
  );
});

// Enhanced Styled Form Components
const FormSection = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3.5),
  backgroundColor: '#FFFFFF',
  borderRadius: 16,
  border: '1px solid #E5E7EB',
  marginBottom: theme.spacing(3.5),
  transition: 'all 0.3s ease',
  boxShadow: '0 1px 3px rgba(0,0,0,0.02)',
  '&:hover': {
    borderColor: alpha(BRAND_COLORS.primary, 0.25),
    boxShadow: `0 4px 20px ${alpha(BRAND_COLORS.primary, 0.06)}`,
    transform: 'translateY(-2px)',
  },
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(2),
  },
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  fontSize: '1.1rem',
  color: BRAND_COLORS.text.primary,
  marginBottom: theme.spacing(3),
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  '& .MuiSvgIcon-root': {
    color: BRAND_COLORS.primary,
    fontSize: 24,
  },
  '&::after': {
    content: '""',
    flex: 1,
    height: '2px',
    background: `linear-gradient(90deg, ${alpha(BRAND_COLORS.primary, 0.2)}, transparent)`,
    marginLeft: theme.spacing(2),
  },
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: 12,
    backgroundColor: '#FFFFFF',
    transition: 'all 0.3s ease',
    fontSize: '0.95rem',
    fontWeight: 500,
    color: BRAND_COLORS.text.primary,
    '&:hover fieldset': {
      borderColor: BRAND_COLORS.primary,
      borderWidth: 2,
    },
    '&.Mui-focused fieldset': {
      borderColor: BRAND_COLORS.primary,
      borderWidth: 2,
      boxShadow: `0 0 0 4px ${alpha(BRAND_COLORS.primary, 0.1)}`,
    },
    '&.Mui-error fieldset': {
      borderColor: BRAND_COLORS.error,
    },
  },
  '& .MuiFormLabel-root': {
    fontWeight: 600,
    color: BRAND_COLORS.text.secondary,
    fontSize: '0.9rem',
    '&.Mui-focused': {
      color: BRAND_COLORS.primary,
    },
    '&.Mui-error': {
      color: BRAND_COLORS.error,
    },
  },
  '& .MuiFormHelperText-root': {
    fontWeight: 500,
    fontSize: '0.75rem',
    marginTop: 6,
  },
  '& .MuiInputBase-input': {
    fontWeight: 500,
    padding: '14px 16px',
  },
}));

// FIXED: Improved StyledSelect with better selection handling
const StyledSelect = styled(Select)(({ theme }) => ({
  borderRadius: 12,
  backgroundColor: '#FFFFFF',
  minWidth: 150,
  fontWeight: 500,
  fontSize: '0.95rem',
  '& .MuiSelect-select': {
    padding: '14px 16px',
    fontWeight: 500,
    minHeight: '1.5rem',
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: BRAND_COLORS.primary,
    borderWidth: 2,
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: BRAND_COLORS.primary,
    borderWidth: 2,
    boxShadow: `0 0 0 4px ${alpha(BRAND_COLORS.primary, 0.1)}`,
  },
  '& .MuiSelect-icon': {
    color: BRAND_COLORS.primary,
  },
}));

const StyledFormControl = styled(FormControl)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: 12,
    backgroundColor: '#FFFFFF',
    minWidth: 150,
    fontWeight: 500,
    fontSize: '0.95rem',
    '&:hover fieldset': {
      borderColor: BRAND_COLORS.primary,
      borderWidth: 2,
    },
    '&.Mui-focused fieldset': {
      borderColor: BRAND_COLORS.primary,
      borderWidth: 2,
      boxShadow: `0 0 0 4px ${alpha(BRAND_COLORS.primary, 0.1)}`,
    },
    '& .MuiSelect-select': {
      padding: '14px 16px',
      fontWeight: 500,
      minHeight: '1.5rem',
    },
  },
  '& .MuiFormLabel-root': {
    fontWeight: 600,
    color: BRAND_COLORS.text.secondary,
    fontSize: '0.9rem',
    '&.Mui-focused': {
      color: BRAND_COLORS.primary,
    },
  },
  '& .MuiFormHelperText-root': {
    fontWeight: 500,
    fontSize: '0.75rem',
    marginTop: 6,
  },
}));

const ImageUploadBox = styled(Box)(({ theme, error, dragActive }) => ({
  border: `2px dashed ${error ? BRAND_COLORS.error : (dragActive ? BRAND_COLORS.primary : '#D1D5DB')}`,
  borderRadius: 16,
  padding: theme.spacing(4),
  textAlign: 'center',
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  backgroundColor: dragActive ? alpha(BRAND_COLORS.primary, 0.05) : '#FAFBFC',
  minHeight: 160,
  maxHeight: 300,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  '&:hover': {
    borderColor: BRAND_COLORS.primary,
    backgroundColor: alpha(BRAND_COLORS.primary, 0.03),
    transform: 'scale(1.005)',
  },
  ...(error && {
    borderColor: BRAND_COLORS.error,
    backgroundColor: alpha(BRAND_COLORS.error, 0.04),
  }),
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(2),
    minHeight: 120,
  },
}));

const StyledCard = styled(Paper)(({ theme }) => ({
  borderRadius: 20,
  border: '1px solid #E5E7EB',
  overflow: 'hidden',
  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
  boxShadow: '0 1px 3px rgba(0,0,0,0.02)',
  '&:hover': {
    boxShadow: `0 8px 40px ${alpha(BRAND_COLORS.primary, 0.06)}`,
  },
}));

// Enhanced Navigation Buttons
const NavButton = styled(Button)(({ theme, variant = 'contained' }) => ({
  borderRadius: 12,
  textTransform: 'none',
  fontWeight: 700,
  padding: '12px 32px',
  fontSize: '0.95rem',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: `0 8px 30px ${alpha(BRAND_COLORS.primary, 0.25)}`,
  },
  '&:active': {
    transform: 'scale(0.98)',
  },
  [theme.breakpoints.down('sm')]: {
    padding: '10px 20px',
    fontSize: '0.85rem',
  },
}));

// ============================================
// API CONFIG
// ============================================

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

const API_ENDPOINTS = {
  PRODUCTS: {
    REGISTER: `${API_BASE_URL}/products/register.php`,
    LIST: `${API_BASE_URL}/products/register.php`,
    UPDATE: (id) => `${API_BASE_URL}/products/register.php?id=${id}`,
    DELETE: (id) => `${API_BASE_URL}/products/register.php?id=${id}`,
  },
};

// ============================================
// CONSTANTS
// ============================================

const UNITS_OF_MEASURE = [
  // Weight/Mass
  { value: 'kg', label: 'Kilogram (kg)', category: 'Weight' },
  { value: 'g', label: 'Gram (g)', category: 'Weight' },
  { value: 'mg', label: 'Milligram (mg)', category: 'Weight' },
  { value: 'lb', label: 'Pound (lb)', category: 'Weight' },
  { value: 'oz', label: 'Ounce (oz)', category: 'Weight' },
  { value: 'ton', label: 'Metric Ton (t)', category: 'Weight' },
  { value: 'ton_us', label: 'US Ton (ton)', category: 'Weight' },
  
  // Volume
  { value: 'L', label: 'Liter (L)', category: 'Volume' },
  { value: 'mL', label: 'Milliliter (mL)', category: 'Volume' },
  { value: 'gal', label: 'Gallon (gal)', category: 'Volume' },
  { value: 'qt', label: 'Quart (qt)', category: 'Volume' },
  { value: 'pt', label: 'Pint (pt)', category: 'Volume' },
  { value: 'fl_oz', label: 'Fluid Ounce (fl oz)', category: 'Volume' },
  { value: 'm3', label: 'Cubic Meter (m³)', category: 'Volume' },
  { value: 'ft3', label: 'Cubic Foot (ft³)', category: 'Volume' },
  { value: 'bbl', label: 'Barrel (bbl)', category: 'Volume' },
  
  // Length/Dimension
  { value: 'm', label: 'Meter (m)', category: 'Length' },
  { value: 'cm', label: 'Centimeter (cm)', category: 'Length' },
  { value: 'mm', label: 'Millimeter (mm)', category: 'Length' },
  { value: 'ft', label: 'Foot (ft)', category: 'Length' },
  { value: 'in', label: 'Inch (in)', category: 'Length' },
  
  // Area
  { value: 'm2', label: 'Square Meter (m²)', category: 'Area' },
  { value: 'ft2', label: 'Square Foot (ft²)', category: 'Area' },
  { value: 'ha', label: 'Hectare (ha)', category: 'Area' },
  
  // Concentration
  { value: 'mol', label: 'Mole (mol)', category: 'Concentration' },
  { value: 'mmol', label: 'Millimole (mmol)', category: 'Concentration' },
  { value: 'eq', label: 'Equivalent (eq)', category: 'Concentration' },
  { value: 'M', label: 'Molar (M)', category: 'Concentration' },
  { value: 'mM', label: 'Millimolar (mM)', category: 'Concentration' },
  { value: '%', label: 'Percent (%)', category: 'Concentration' },
  { value: 'ppm', label: 'Parts Per Million (ppm)', category: 'Concentration' },
  { value: 'ppb', label: 'Parts Per Billion (ppb)', category: 'Concentration' },
  
  // Count/Pieces
  { value: 'pcs', label: 'Pieces (pcs)', category: 'Count' },
  { value: 'box', label: 'Box (box)', category: 'Count' },
  { value: 'case', label: 'Case (case)', category: 'Count' },
  { value: 'pallet', label: 'Pallet (pallet)', category: 'Count' },
  { value: 'drum', label: 'Drum (drum)', category: 'Count' },
  { value: 'bag', label: 'Bag (bag)', category: 'Count' },
  { value: 'bottle', label: 'Bottle (bottle)', category: 'Count' },
  { value: 'canister', label: 'Canister (canister)', category: 'Count' },
  { value: 'cylinder', label: 'Cylinder (cylinder)', category: 'Count' },
  
  // Energy
  { value: 'kWh', label: 'Kilowatt-hour (kWh)', category: 'Energy' },
  { value: 'MJ', label: 'Megajoule (MJ)', category: 'Energy' },
  { value: 'BTU', label: 'British Thermal Unit (BTU)', category: 'Energy' },
  
  // Flow Rate
  { value: 'L_min', label: 'Liters per Minute (L/min)', category: 'Flow Rate' },
  { value: 'gal_min', label: 'Gallons per Minute (gal/min)', category: 'Flow Rate' },
  { value: 'm3_h', label: 'Cubic Meters per Hour (m³/h)', category: 'Flow Rate' },
  
  // Pressure
  { value: 'psi', label: 'Pounds per Square Inch (psi)', category: 'Pressure' },
  { value: 'bar', label: 'Bar (bar)', category: 'Pressure' },
  { value: 'kPa', label: 'Kilopascal (kPa)', category: 'Pressure' },
  { value: 'MPa', label: 'Megapascal (MPa)', category: 'Pressure' },
  { value: 'atm', label: 'Atmosphere (atm)', category: 'Pressure' },
  
  // Temperature
  { value: 'C', label: 'Celsius (°C)', category: 'Temperature' },
  { value: 'F', label: 'Fahrenheit (°F)', category: 'Temperature' },
  { value: 'K', label: 'Kelvin (K)', category: 'Temperature' },
  
  // Viscosity
  { value: 'cP', label: 'Centipoise (cP)', category: 'Viscosity' },
  { value: 'Pa_s', label: 'Pascal-second (Pa·s)', category: 'Viscosity' },
  
  // Density
  { value: 'g_cm3', label: 'Grams per Cubic Centimeter (g/cm³)', category: 'Density' },
  { value: 'kg_m3', label: 'Kilograms per Cubic Meter (kg/m³)', category: 'Density' },
  { value: 'lb_ft3', label: 'Pounds per Cubic Foot (lb/ft³)', category: 'Density' },
  
  // pH
  { value: 'pH', label: 'pH Units', category: 'pH' },
  
  // Conductivity
  { value: 'uS_cm', label: 'Microsiemens per Centimeter (µS/cm)', category: 'Conductivity' },
  { value: 'mS_cm', label: 'Millisiens per Centimeter (mS/cm)', category: 'Conductivity' },
];

// Group units by category for better organization
const UNITS_BY_CATEGORY = UNITS_OF_MEASURE.reduce((acc, unit) => {
  if (!acc[unit.category]) {
    acc[unit.category] = [];
  }
  acc[unit.category].push(unit);
  return acc;
}, {});

const APPLICATIONS = [
  'Food & Beverages',
  'Water Treatment',
  'Energy & Battery',
  'Electronics & Semiconductor',
  'Personal Care & Cosmetics',
  'Pharmaceuticals',
  'Agriculture',
  'Manufacturing',
];

const PRODUCT_TYPES = [
  'Solvents', 'Catalysts', 'Intermediates', 'Acids', 'Alkalis',
  'Corrosion Inhibitors', 'Surfactants', 'Pharmaceutical Ingredients',
  'Excipients', 'Diagnostic Reagents', 'Disinfectants', 'Electrolytes',
  'Fuel Additives',
];

const HAZARD_CLASSIFICATIONS = [
  'Flammable', 'Toxic', 'Corrosive', 'Reactive',
  'Explosives', 'Irritation', 'Oxidizing',
];

const GRADES = ['Industrial', 'Pharmaceutical', 'Food', 'Others'];
const CERTIFICATIONS = ['ISO', 'REACH', 'FDA', 'GMP', 'HACCP'];
const CURRENCIES = ['NGN', 'USD', 'EUR', 'GBP'];
const DELIVERY_TIMES = [
  '1-3 days',
  '4-7 days',
  '1-2 weeks',
  '2-3 weeks',
  '3-4 weeks',
  '1-2 months',
  '2-3 months',
  '3+ months',
];

// ============================================
// CHEMICAL FORMULA CONSTANTS
// ============================================

const COMMON_FORMULAS = [
  { name: 'Sulfuric Acid', formula: 'H₂SO₄', plain: 'H2SO4', category: 'Acids' },
  { name: 'Hydrochloric Acid', formula: 'HCl', plain: 'HCl', category: 'Acids' },
  { name: 'Nitric Acid', formula: 'HNO₃', plain: 'HNO3', category: 'Acids' },
  { name: 'Phosphoric Acid', formula: 'H₃PO₄', plain: 'H3PO4', category: 'Acids' },
  { name: 'Acetic Acid', formula: 'CH₃COOH', plain: 'CH3COOH', category: 'Acids' },
  { name: 'Citric Acid', formula: 'C₆H₈O₇', plain: 'C6H8O7', category: 'Acids' },
  { name: 'Sodium Hydroxide', formula: 'NaOH', plain: 'NaOH', category: 'Bases' },
  { name: 'Potassium Hydroxide', formula: 'KOH', plain: 'KOH', category: 'Bases' },
  { name: 'Ammonia', formula: 'NH₃', plain: 'NH3', category: 'Bases' },
  { name: 'Calcium Hydroxide', formula: 'Ca(OH)₂', plain: 'Ca(OH)2', category: 'Bases' },
  { name: 'Hydrogen Peroxide', formula: 'H₂O₂', plain: 'H2O2', category: 'Oxidizers' },
  { name: 'Sodium Chloride', formula: 'NaCl', plain: 'NaCl', category: 'Salts' },
  { name: 'Calcium Carbonate', formula: 'CaCO₃', plain: 'CaCO3', category: 'Salts' },
  { name: 'Sodium Carbonate', formula: 'Na₂CO₃', plain: 'Na2CO3', category: 'Salts' },
  { name: 'Sodium Bicarbonate', formula: 'NaHCO₃', plain: 'NaHCO3', category: 'Salts' },
  { name: 'Methanol', formula: 'CH₃OH', plain: 'CH3OH', category: 'Alcohols' },
  { name: 'Ethanol', formula: 'C₂H₅OH', plain: 'C2H5OH', category: 'Alcohols' },
  { name: 'Isopropanol', formula: 'C₃H₇OH', plain: 'C3H7OH', category: 'Alcohols' },
  { name: 'Ethylene Glycol', formula: 'C₂H₆O₂', plain: 'C2H6O2', category: 'Glycols' },
  { name: 'Propylene Glycol', formula: 'C₃H₈O₂', plain: 'C3H8O2', category: 'Glycols' },
  { name: 'Glycerol', formula: 'C₃H₈O₃', plain: 'C3H8O3', category: 'Glycols' },
  { name: 'Acetone', formula: 'CH₃COCH₃', plain: 'CH3COCH3', category: 'Ketones' },
  { name: 'Methyl Ethyl Ketone', formula: 'CH₃COC₂H₅', plain: 'CH3COC2H5', category: 'Ketones' },
  { name: 'Benzene', formula: 'C₆H₆', plain: 'C6H6', category: 'Aromatics' },
  { name: 'Toluene', formula: 'C₇H₈', plain: 'C7H8', category: 'Aromatics' },
  { name: 'Xylene', formula: 'C₈H₁₀', plain: 'C8H10', category: 'Aromatics' },
  { name: 'Phenol', formula: 'C₆H₅OH', plain: 'C6H5OH', category: 'Aromatics' },
  { name: 'Formaldehyde', formula: 'CH₂O', plain: 'CH2O', category: 'Aldehydes' },
  { name: 'Glucose', formula: 'C₆H₁₂O₆', plain: 'C6H12O6', category: 'Carbohydrates' },
  { name: 'Sucrose', formula: 'C₁₂H₂₂O₁₁', plain: 'C12H22O11', category: 'Carbohydrates' },
  { name: 'Urea', formula: 'CH₄N₂O', plain: 'CH4N2O', category: 'Organics' },
  { name: 'Sodium Sulfate', formula: 'Na₂SO₄', plain: 'Na2SO4', category: 'Salts' },
  { name: 'Magnesium Sulfate', formula: 'MgSO₄', plain: 'MgSO4', category: 'Salts' },
  { name: 'Potassium Permanganate', formula: 'KMnO₄', plain: 'KMnO4', category: 'Oxidizers' },
  { name: 'Sodium Dichromate', formula: 'Na₂Cr₂O₇', plain: 'Na2Cr2O7', category: 'Oxidizers' },
  { name: 'Ammonium Nitrate', formula: 'NH₄NO₃', plain: 'NH4NO3', category: 'Fertilizers' },
  { name: 'Calcium Nitrate', formula: 'Ca(NO₃)₂', plain: 'Ca(NO3)2', category: 'Fertilizers' },
  { name: 'Potassium Chloride', formula: 'KCl', plain: 'KCl', category: 'Fertilizers' },
];

// ============================================
// CHEMICAL FORMULA COMPONENT
// ============================================

const ChemicalFormulaInput = React.memo(({ value, onChange, onBlur, error, touched, errors, required }) => {
  const [formulaPreview, setFormulaPreview] = useState('');
  const [showFormulaSelector, setShowFormulaSelector] = useState(false);
  const [formulaHistory, setFormulaHistory] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('All');
  const [anchorEl, setAnchorEl] = useState(null);
  const inputRef = useRef(null);

  const categories = ['All', ...new Set(COMMON_FORMULAS.map(f => f.category))];

  useEffect(() => {
    try {
      const saved = localStorage.getItem('formula_history');
      if (saved) {
        setFormulaHistory(JSON.parse(saved));
      }
    } catch (e) {
      console.debug('Error loading formula history:', e);
    }
  }, []);

  const saveToHistory = useCallback((formula) => {
    if (!formula || formula.trim() === '') return;
    setFormulaHistory(prev => {
      const newHistory = [formula, ...prev.filter(f => f !== formula)].slice(0, 10);
      try {
        localStorage.setItem('formula_history', JSON.stringify(newHistory));
      } catch (e) {
        console.debug('Error saving formula history:', e);
      }
      return newHistory;
    });
  }, []);

  const formatFormula = useCallback((formula) => {
    if (!formula) return '';

    let formatted = formula;
    
    const subscriptMap = {
      '₀': '0', '₁': '1', '₂': '2', '₃': '3', '₄': '4',
      '₅': '5', '₆': '6', '₇': '7', '₈': '8', '₉': '9'
    };

    Object.keys(subscriptMap).forEach(key => {
      formatted = formatted.replace(new RegExp(key, 'g'), `<sub>${subscriptMap[key]}</sub>`);
    });

    formatted = formatted.replace(/([A-Za-z\)])(\d+)/g, (match, char, num) => {
      return char + num.split('').map(d => `<sub>${d}</sub>`).join('');
    });

    formatted = formatted.replace(/\(([^)]+)\)(\d+)/g, (match, group, num) => {
      return `(${group})` + num.split('').map(d => `<sub>${d}</sub>`).join('');
    });

    formatted = formatted.replace(/\^([+-]?\d+)/g, (match, charge) => {
      return `<sup>${charge}</sup>`;
    });

    return formatted;
  }, []);

  useEffect(() => {
    setFormulaPreview(formatFormula(value));
  }, [value, formatFormula]);

  const handleFormulaSelect = useCallback((formula) => {
    onChange('Formula', formula);
    setShowFormulaSelector(false);
    saveToHistory(formula);
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, [onChange, saveToHistory]);

  const handleFormulaChange = useCallback((e) => {
    const val = e.target.value;
    onChange('Formula', val);
  }, [onChange]);

  const handleBlur = useCallback(() => {
    onBlur('Formula');
    if (value && value.trim() !== '') {
      saveToHistory(value);
    }
  }, [onBlur, value, saveToHistory]);

  const handleCopy = useCallback(() => {
    if (value) {
      navigator.clipboard.writeText(value).then(() => {
        console.debug('Formula copied to clipboard:', value);
      }).catch(err => {
        console.debug('Error copying formula:', err);
      });
    }
  }, [value]);

  const filteredFormulas = useMemo(() => {
    let filtered = COMMON_FORMULAS;
    
    if (activeCategory !== 'All') {
      filtered = filtered.filter(f => f.category === activeCategory);
    }
    
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter(f => 
        f.name.toLowerCase().includes(query) ||
        f.formula.toLowerCase().includes(query) ||
        f.plain.toLowerCase().includes(query) ||
        f.category.toLowerCase().includes(query)
      );
    }
    
    return filtered;
  }, [activeCategory, searchQuery]);

  const categoryCounts = useMemo(() => {
    const counts = { All: COMMON_FORMULAS.length };
    categories.forEach(cat => {
      if (cat !== 'All') {
        counts[cat] = COMMON_FORMULAS.filter(f => f.category === cat).length;
      }
    });
    return counts;
  }, [categories]);

  return (
    <Box>
      <StyledTextField
        fullWidth
        label="Chemical Formula"
        name="Formula"
        inputRef={inputRef}
        value={value || ''}
        onChange={handleFormulaChange}
        onBlur={handleBlur}
        error={!!errors.Formula && touched?.Formula}
        helperText={touched?.Formula && errors.Formula}
        placeholder="e.g., H2SO4, C6H12O6, CH3COOH"
        required={required}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Typography variant="body2" color="textSecondary" sx={{ fontWeight: 700 }}>
                Formula:
              </Typography>
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end">
              {value && (
                <Tooltip title="Copy formula">
                  <IconButton 
                    onClick={handleCopy}
                    size="small"
                    sx={{ 
                      mr: 0.5,
                      color: BRAND_COLORS.primary,
                      '&:hover': { 
                        bgcolor: alpha(BRAND_COLORS.primary, 0.1),
                        transform: 'scale(1.1)',
                      },
                      transition: 'all 0.2s ease',
                    }}
                  >
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              <Tooltip title="Select from common formulas">
                <IconButton 
                  onClick={(e) => {
                    setAnchorEl(e.currentTarget);
                    setShowFormulaSelector(true);
                  }}
                  size="small"
                  sx={{ 
                    color: BRAND_COLORS.primary,
                    '&:hover': { 
                      bgcolor: alpha(BRAND_COLORS.primary, 0.1),
                      transform: 'scale(1.1)',
                    },
                    transition: 'all 0.2s ease',
                  }}
                >
                  <ScienceIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </InputAdornment>
          ),
        }}
      />
      
      {value && value.trim() !== '' && (
        <Box 
          sx={{ 
            mt: 2, 
            p: 2.5, 
            bgcolor: alpha(BRAND_COLORS.primary, 0.04),
            borderRadius: 3,
            border: `1px solid ${alpha(BRAND_COLORS.primary, 0.12)}`,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            flexWrap: 'wrap',
            transition: 'all 0.3s ease',
          }}
        >
          <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 700, fontSize: '0.75rem' }}>
            Preview:
          </Typography>
          <Typography 
            variant="body1" 
            sx={{ 
              fontWeight: 700,
              fontFamily: 'monospace',
              fontSize: '1.15rem',
              color: BRAND_COLORS.primary,
              '& sub': { fontSize: '0.7rem' },
              '& sup': { fontSize: '0.7rem' }
            }}
            dangerouslySetInnerHTML={{ __html: formulaPreview || value }}
          />
          <Chip 
            label="✓ Valid formula" 
            size="small" 
            icon={<CheckIcon sx={{ fontSize: 14 }} />}
            sx={{ 
              bgcolor: alpha(BRAND_COLORS.success, 0.12),
              color: BRAND_COLORS.success,
              fontWeight: 600,
              fontSize: '0.65rem',
              borderRadius: 6,
              '& .MuiChip-icon': {
                color: BRAND_COLORS.success,
              },
            }}
          />
        </Box>
      )}

      <Popover
        open={showFormulaSelector}
        anchorEl={anchorEl}
        onClose={() => {
          setShowFormulaSelector(false);
          setAnchorEl(null);
          setSearchQuery('');
          setActiveCategory('All');
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{
          sx: {
            width: 500,
            maxHeight: 520,
            borderRadius: 16,
            boxShadow: '0 12px 48px rgba(0,0,0,0.15)',
            p: 3,
            overflow: 'hidden',
            border: '1px solid #E5E7EB',
            [theme => theme.breakpoints.down('sm')]: {
              width: 'calc(100vw - 32px)',
              maxHeight: '80vh',
              p: 2,
            },
          },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
          <ScienceIcon sx={{ color: BRAND_COLORS.primary, fontSize: 28 }} />
          <Typography variant="h6" fontWeight={700} sx={{ color: BRAND_COLORS.primary }}>
            Select Chemical Formula
          </Typography>
        </Box>
        
        <TextField
          size="small"
          placeholder="Search formulas by name or formula..."
          fullWidth
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{ mb: 2.5 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" color="action" />
              </InputAdornment>
            ),
            sx: {
              borderRadius: 2,
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
              },
            },
          }}
        />
        
        <Box sx={{ mb: 2.5, overflow: 'auto', display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
          {categories.map((cat) => (
            <Chip
              key={cat}
              label={`${cat} (${categoryCounts[cat] || 0})`}
              size="small"
              onClick={() => setActiveCategory(cat)}
              color={activeCategory === cat ? 'primary' : 'default'}
              variant={activeCategory === cat ? 'filled' : 'outlined'}
              sx={{ 
                fontWeight: activeCategory === cat ? 700 : 500,
                borderRadius: 6,
                '&:hover': { 
                  bgcolor: activeCategory === cat ? undefined : alpha(BRAND_COLORS.primary, 0.08) 
                },
                transition: 'all 0.2s ease',
              }}
            />
          ))}
        </Box>
        
        {formulaHistory.length > 0 && searchQuery === '' && activeCategory === 'All' && (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <HistoryIcon fontSize="small" sx={{ color: BRAND_COLORS.primary }} />
              <Typography variant="caption" color="textSecondary" fontWeight={700}>
                Recently Used
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mb: 2.5 }}>
              {formulaHistory.slice(0, 5).map((formula, index) => (
                <Chip
                  key={`history-${index}`}
                  label={formula}
                  size="small"
                  onClick={() => handleFormulaSelect(formula)}
                  sx={{ 
                    fontFamily: 'monospace',
                    fontWeight: 600,
                    bgcolor: alpha(BRAND_COLORS.primary, 0.06),
                    border: `1px solid ${alpha(BRAND_COLORS.primary, 0.12)}`,
                    '&:hover': { 
                      bgcolor: alpha(BRAND_COLORS.primary, 0.15),
                      transform: 'scale(1.02)',
                    },
                    transition: 'all 0.2s ease',
                    borderRadius: 6,
                  }}
                />
              ))}
            </Box>
            <Divider sx={{ my: 2 }} />
          </>
        )}
        
        <Box sx={{ maxHeight: 280, overflow: 'auto', '&::-webkit-scrollbar': { width: 6 }, '&::-webkit-scrollbar-thumb': { borderRadius: 3, bgcolor: alpha(BRAND_COLORS.primary, 0.2) } }}>
          {filteredFormulas.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="body2" color="textSecondary" fontWeight={500}>
                No formulas found matching your search
              </Typography>
            </Box>
          ) : (
            <List dense disablePadding>
              {filteredFormulas.map((item, index) => (
                <ListItem
                  key={index}
                  button
                  onClick={() => handleFormulaSelect(item.plain)}
                  sx={{
                    borderRadius: 2,
                    mb: 0.5,
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      bgcolor: alpha(BRAND_COLORS.primary, 0.08),
                      transform: 'translateX(4px)',
                    },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <ScienceIcon fontSize="small" color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                        <Typography variant="body2" fontWeight={600}>
                          {item.name}
                        </Typography>
                        <Chip 
                          label={item.category} 
                          size="small" 
                          sx={{ 
                            height: 20,
                            fontSize: '0.6rem',
                            fontWeight: 600,
                            bgcolor: alpha(BRAND_COLORS.info, 0.08),
                            color: BRAND_COLORS.info,
                            borderRadius: 4,
                          }}
                        />
                      </Box>
                    }
                    secondary={
                      <Typography 
                        variant="body2" 
                        fontFamily="monospace" 
                        color="primary"
                        fontWeight={600}
                        sx={{ 
                          '& sub': { fontSize: '0.65rem' },
                          '& sup': { fontSize: '0.65rem' },
                          mt: 0.5,
                        }}
                        dangerouslySetInnerHTML={{ 
                          __html: formatFormula(item.formula) 
                        }}
                      />
                    }
                  />
                </ListItem>
              ))}
            </List>
          )}
        </Box>
        
        <Box sx={{ mt: 2.5, p: 2, bgcolor: '#F8FAFC', borderRadius: 2, border: '1px solid #E5E7EB' }}>
          <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 500 }}>
            💡 Type directly or select from the list. Numbers automatically format as subscripts.
          </Typography>
        </Box>
      </Popover>
    </Box>
  );
});

// ============================================
// STEP COMPONENTS
// ============================================

// Step 1: Product Information
const ProductInfoStep = React.memo(({ formData, onChange, errors, onBlur, touched }) => {
  const [showOtherGrade, setShowOtherGrade] = useState(formData.Grade === 'Others');

  const handleGradeChange = useCallback((e) => {
    const value = e.target.value;
    onChange('Grade', value);
    setShowOtherGrade(value === 'Others');
    if (value !== 'Others') {
      onChange('otherGrade', '');
    }
  }, [onChange]);

  return (
    <Box>
      <FormSection>
        <SectionTitle variant="h6">
          <DescriptionIcon />
          Basic Information
        </SectionTitle>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <StyledTextField
              fullWidth
              label="Chemical Name"
              name="chemicalName"
              value={formData.chemicalName || ''}
              onChange={(e) => onChange('chemicalName', e.target.value)}
              onBlur={() => onBlur('chemicalName')}
              error={!!errors.chemicalName && touched?.chemicalName}
              helperText={touched?.chemicalName && errors.chemicalName}
              placeholder="Enter chemical name"
              required
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <StyledTextField
              fullWidth
              label="CAS Number"
              name="CASNumber"
              value={formData.CASNumber || ''}
              onChange={(e) => onChange('CASNumber', e.target.value)}
              onBlur={() => onBlur('CASNumber')}
              error={!!errors.CASNumber && touched?.CASNumber}
              helperText={touched?.CASNumber && errors.CASNumber}
              placeholder="e.g., 7647-01-0"
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Chip 
                      label="CAS" 
                      size="small" 
                      sx={{ 
                        bgcolor: alpha(BRAND_COLORS.primary, 0.1),
                        color: BRAND_COLORS.primary,
                        fontWeight: 700,
                        fontSize: '0.6rem',
                        height: 22,
                        borderRadius: 4,
                      }} 
                    />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <StyledTextField
              fullWidth
              label="Description"
              name="description"
              value={formData.description || ''}
              onChange={(e) => onChange('description', e.target.value)}
              onBlur={() => onBlur('description')}
              error={!!errors.description && touched?.description}
              helperText={touched?.description && errors.description}
              placeholder="Enter product description..."
              multiline
              rows={3}
              required
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <StyledFormControl fullWidth error={!!errors.applications && touched?.applications} required>
              <InputLabel>Applications</InputLabel>
              <StyledSelect
                name="applications"
                value={formData.applications || ''}
                onChange={(e) => onChange('applications', e.target.value)}
                onBlur={() => onBlur('applications')}
                label="Applications"
                sx={{ minWidth: 150 }}
              >
                <MenuItem value="" disabled>Select application</MenuItem>
                {APPLICATIONS.map((app) => (
                  <MenuItem key={app} value={app} sx={{ fontWeight: 500 }}>{app}</MenuItem>
                ))}
              </StyledSelect>
              {touched?.applications && errors.applications && (
                <FormHelperText>{errors.applications}</FormHelperText>
              )}
            </StyledFormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <StyledFormControl fullWidth error={!!errors.productType && touched?.productType} required>
              <InputLabel>Product Type</InputLabel>
              <StyledSelect
                name="productType"
                value={formData.productType || ''}
                onChange={(e) => onChange('productType', e.target.value)}
                onBlur={() => onBlur('productType')}
                label="Product Type"
                sx={{ minWidth: 150 }}
              >
                <MenuItem value="" disabled>Select product type</MenuItem>
                {PRODUCT_TYPES.map((type) => (
                  <MenuItem key={type} value={type} sx={{ fontWeight: 500 }}>{type}</MenuItem>
                ))}
              </StyledSelect>
              {touched?.productType && errors.productType && (
                <FormHelperText>{errors.productType}</FormHelperText>
              )}
            </StyledFormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <StyledFormControl fullWidth error={!!errors.hazardClassifications && touched?.hazardClassifications} required>
              <InputLabel>Hazard Classification</InputLabel>
              <StyledSelect
                name="hazardClassifications"
                value={formData.hazardClassifications || ''}
                onChange={(e) => onChange('hazardClassifications', e.target.value)}
                onBlur={() => onBlur('hazardClassifications')}
                label="Hazard Classification"
                sx={{ minWidth: 150 }}
              >
                <MenuItem value="" disabled>Select hazard classification</MenuItem>
                {HAZARD_CLASSIFICATIONS.map((hazard) => (
                  <MenuItem key={hazard} value={hazard} sx={{ fontWeight: 500 }}>{hazard}</MenuItem>
                ))}
              </StyledSelect>
              {touched?.hazardClassifications && errors.hazardClassifications && (
                <FormHelperText>{errors.hazardClassifications}</FormHelperText>
              )}
            </StyledFormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <ChemicalFormulaInput
              value={formData.Formula || ''}
              onChange={onChange}
              onBlur={onBlur}
              error={!!errors.Formula}
              touched={touched}
              errors={errors}
              required={true}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <StyledTextField
              fullWidth
              label="Synonyms"
              name="Synonyms"
              value={formData.Synonyms || ''}
              onChange={(e) => onChange('Synonyms', e.target.value)}
              placeholder="Enter synonyms (comma separated)"
              helperText="Alternative names for the chemical"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <StyledFormControl fullWidth error={!!errors.Grade && touched?.Grade} required>
              <InputLabel>Grade</InputLabel>
              <StyledSelect
                name="Grade"
                value={formData.Grade || ''}
                onChange={handleGradeChange}
                onBlur={() => onBlur('Grade')}
                label="Grade"
                sx={{ minWidth: 150 }}
              >
                <MenuItem value="" disabled>Select Grade</MenuItem>
                {GRADES.map((grade) => (
                  <MenuItem key={grade} value={grade} sx={{ fontWeight: 500 }}>{grade}</MenuItem>
                ))}
              </StyledSelect>
              {touched?.Grade && errors.Grade && (
                <FormHelperText>{errors.Grade}</FormHelperText>
              )}
            </StyledFormControl>
            {showOtherGrade && (
              <StyledTextField
                fullWidth
                sx={{ mt: 2 }}
                label="Please specify grade"
                name="otherGrade"
                value={formData.otherGrade || ''}
                onChange={(e) => onChange('otherGrade', e.target.value)}
                onBlur={() => onBlur('otherGrade')}
                error={!!errors.otherGrade && touched?.otherGrade}
                helperText={touched?.otherGrade && errors.otherGrade}
                placeholder="Enter grade specification"
                required
                size="small"
              />
            )}
          </Grid>
        </Grid>
      </FormSection>

      <FormSection>
        <SectionTitle variant="h6">
          <ImageIcon />
          Product Image
        </SectionTitle>
        <ImageUpload
          image={formData.image}
          onChange={onChange}
          error={errors.image || formData.imageError}
          onBlur={() => onBlur('image')}
        />
        <Typography variant="caption" color="textSecondary" sx={{ mt: 1.5, display: 'block', fontWeight: 500 }}>
          Upload a clear image of the product. Supported formats: JPG, PNG, GIF, WEBP, SVG (Max 5MB)
        </Typography>
      </FormSection>
    </Box>
  );
});

// Image Upload Component
const ImageUpload = React.memo(({ image, onChange, error, onBlur }) => {
  const [dragActive, setDragActive] = useState(false);
  const [preview, setPreview] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (image && typeof image === 'string' && image.startsWith('data:')) {
      setPreview(image);
    } else if (image && typeof image === 'string' && image.startsWith('http')) {
      setPreview(image);
    } else if (image && typeof image === 'object' && image instanceof File) {
      const reader = new FileReader();
      reader.onload = (e) => setPreview(e.target.result);
      reader.readAsDataURL(image);
    } else {
      setPreview(null);
    }
  }, [image]);

  const handleFileChange = useCallback((file) => {
    if (!file) return;

    const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'];
    if (!validTypes.includes(file.type)) {
      onChange('imageError', 'Invalid file type. Please upload JPG, PNG, GIF, WEBP, or SVG.');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      onChange('imageError', 'File too large. Maximum size is 5MB.');
      return;
    }

    onChange('image', file);
    onChange('imageError', '');
    
    const reader = new FileReader();
    reader.onload = (e) => {
      setPreview(e.target.result);
    };
    reader.readAsDataURL(file);
  }, [onChange]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragActive(false);
    const file = e.dataTransfer.files[0];
    handleFileChange(file);
  }, [handleFileChange]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setDragActive(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setDragActive(false);
  }, []);

  const handleClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleRemove = useCallback(() => {
    setPreview(null);
    onChange('image', null);
    onChange('imageError', '');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [onChange]);

  return (
    <Box>
      <ImageUploadBox
        error={!!error}
        dragActive={dragActive}
        onClick={handleClick}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={(e) => {
            const file = e.target.files[0];
            if (file) {
              handleFileChange(file);
            }
          }}
          style={{ display: 'none' }}
        />
        
        {preview ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, width: '100%', justifyContent: 'center', flexWrap: 'wrap' }}>
            <Box
              component="img"
              src={preview}
              alt="Product preview"
              sx={{
                width: 140,
                height: 140,
                objectFit: 'contain',
                borderRadius: 3,
                border: '2px solid #E5E7EB',
                boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
              }}
            />
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Typography variant="body2" fontWeight={700} color="text.primary">
                ✓ Image uploaded
              </Typography>
              <Box sx={{ display: 'flex', gap: 1.5 }}>
                <Button
                  variant="contained"
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    fileInputRef.current?.click();
                  }}
                  sx={{ 
                    borderRadius: 8, 
                    textTransform: 'none', 
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    bgcolor: BRAND_COLORS.primary,
                    '&:hover': { bgcolor: BRAND_COLORS.primaryDark },
                  }}
                >
                  Change
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemove();
                  }}
                  sx={{ 
                    borderRadius: 8, 
                    textTransform: 'none', 
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    borderColor: BRAND_COLORS.error,
                    '&:hover': { 
                      bgcolor: alpha(BRAND_COLORS.error, 0.04),
                      borderColor: BRAND_COLORS.error,
                    },
                  }}
                >
                  Remove
                </Button>
              </Box>
            </Box>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Avatar
              sx={{
                width: 56,
                height: 56,
                bgcolor: alpha(BRAND_COLORS.primary, 0.08),
                color: BRAND_COLORS.primary,
              }}
            >
              <AddPhotoIcon sx={{ fontSize: 28 }} />
            </Avatar>
            <Box sx={{ textAlign: 'left' }}>
              <Typography variant="body1" fontWeight={700}>
                Click or drag to upload
              </Typography>
              <Typography variant="caption" color="textSecondary" fontWeight={500}>
                PNG, JPG, WEBP, SVG up to 5MB
              </Typography>
            </Box>
          </Box>
        )}
      </ImageUploadBox>
      {error && (
        <FormHelperText error sx={{ mt: 1.5, fontSize: '0.75rem', fontWeight: 600 }}>
          {error}
        </FormHelperText>
      )}
    </Box>
  );
});

// FIXED: Step 2 - Manufacturers & Suppliers with proper unit selection
const ManufacturerSupplierStep = React.memo(({ formData, onChange, errors, onBlur, touched }) => {
  const discountedPrice = useMemo(() => {
    if (formData.price && formData.Discounts > 0) {
      return formData.price - (formData.price * formData.Discounts / 100);
    }
    return null;
  }, [formData.price, formData.Discounts]);

  const getUnitLabel = useCallback((value) => {
    const unit = UNITS_OF_MEASURE.find(u => u.value === value);
    return unit ? unit.label : value;
  }, []);

  // FIXED: Handle unit selection properly
  const handleUnitChange = useCallback((e) => {
    const value = e.target.value;
    onChange('unit_of_measure', value);
    // Clear any previous error
    if (errors.unit_of_measure) {
      onBlur('unit_of_measure');
    }
  }, [onChange, errors, onBlur]);

  return (
    <Box>
      <FormSection>
        <SectionTitle variant="h6">
          <LocalShippingIcon />
          Supply Chain Information
        </SectionTitle>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <StyledTextField
              fullWidth
              label="Manufacturer Name"
              name="ManufacturerName"
              value={formData.ManufacturerName || ''}
              onChange={(e) => onChange('ManufacturerName', e.target.value)}
              onBlur={() => onBlur('ManufacturerName')}
              error={!!errors.ManufacturerName && touched?.ManufacturerName}
              helperText={touched?.ManufacturerName && errors.ManufacturerName}
              placeholder="Enter manufacturer name"
              required
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <StyledTextField
              fullWidth
              label="Country of Origin"
              name="Country_ofOrigin"
              value={formData.Country_ofOrigin || ''}
              onChange={(e) => onChange('Country_ofOrigin', e.target.value)}
              onBlur={() => onBlur('Country_ofOrigin')}
              error={!!errors.Country_ofOrigin && touched?.Country_ofOrigin}
              helperText={touched?.Country_ofOrigin && errors.Country_ofOrigin}
              placeholder="Enter country of origin"
              required
            />
          </Grid>
          {/* ✅ NEW: HS Code field */}
          <Grid item xs={12} md={6}>
            <StyledTextField
              fullWidth
              label="HS Code"
              name="hs_code"
              value={formData.hs_code || ''}
              onChange={(e) => onChange('hs_code', e.target.value)}
              onBlur={() => onBlur('hs_code')}
              error={!!errors.hs_code && touched?.hs_code}
              helperText={
                (touched?.hs_code && errors.hs_code) ||
                'Harmonized System Code for international trade (e.g., 2807.00)'
              }
              placeholder="e.g., 2807.00"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Chip
                      label="HS"
                      size="small"
                      sx={{
                        bgcolor: alpha(BRAND_COLORS.primary, 0.1),
                        color: BRAND_COLORS.primary,
                        fontWeight: 700,
                        fontSize: '0.6rem',
                        height: 22,
                        borderRadius: 4,
                      }}
                    />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title="Harmonized System codes classify traded products internationally">
                      <PublicIcon fontSize="small" sx={{ color: BRAND_COLORS.text.secondary }} />
                    </Tooltip>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <StyledFormControl fullWidth error={!!errors.Certifications && touched?.Certifications} required>
              <InputLabel>Certifications</InputLabel>
              <StyledSelect
                name="Certifications"
                value={formData.Certifications || ''}
                onChange={(e) => onChange('Certifications', e.target.value)}
                onBlur={() => onBlur('Certifications')}
                label="Certifications"
                sx={{ minWidth: 150 }}
              >
                <MenuItem value="" disabled>Select certifications</MenuItem>
                {CERTIFICATIONS.map((cert) => (
                  <MenuItem key={cert} value={cert} sx={{ fontWeight: 500 }}>{cert}</MenuItem>
                ))}
              </StyledSelect>
              {touched?.Certifications && errors.Certifications && (
                <FormHelperText>{errors.Certifications}</FormHelperText>
              )}
            </StyledFormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <StyledFormControl fullWidth error={!!errors.DeliveryTime && touched?.DeliveryTime} required>
              <InputLabel>Delivery Time</InputLabel>
              <StyledSelect
                name="DeliveryTime"
                value={formData.DeliveryTime || ''}
                onChange={(e) => onChange('DeliveryTime', e.target.value)}
                onBlur={() => onBlur('DeliveryTime')}
                label="Delivery Time"
                sx={{ minWidth: 150 }}
              >
                <MenuItem value="" disabled>Select delivery time</MenuItem>
                {DELIVERY_TIMES.map((time) => (
                  <MenuItem key={time} value={time} sx={{ fontWeight: 500 }}>{time}</MenuItem>
                ))}
              </StyledSelect>
              {touched?.DeliveryTime && errors.DeliveryTime && (
                <FormHelperText>{errors.DeliveryTime}</FormHelperText>
              )}
            </StyledFormControl>
          </Grid>
        </Grid>
      </FormSection>

      <FormSection>
        <SectionTitle variant="h6">
          <MoneyIcon />
          Pricing Information
        </SectionTitle>
        <Grid container spacing={3}>
          {/* FIXED: Unit of Measure Field with proper selection handling */}
          <Grid item xs={12} md={4}>
            <StyledFormControl 
              fullWidth 
              error={!!errors.unit_of_measure && touched?.unit_of_measure} 
              required
            >
              <InputLabel>Unit of Measure</InputLabel>
              <StyledSelect
                name="unit_of_measure"
                value={formData.unit_of_measure || ''}
                onChange={handleUnitChange}
                onBlur={() => onBlur('unit_of_measure')}
                label="Unit of Measure"
                sx={{ minWidth: 150 }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      maxHeight: 400,
                      width: 300,
                      borderRadius: 12,
                      mt: 1,
                    },
                  },
                  anchorOrigin: {
                    vertical: 'bottom',
                    horizontal: 'left',
                  },
                  transformOrigin: {
                    vertical: 'top',
                    horizontal: 'left',
                  },
                }}
              >
                <MenuItem value="" disabled>
                  <em>Select unit</em>
                </MenuItem>
                {Object.entries(UNITS_BY_CATEGORY).map(([category, units]) => [
                  <MenuItem 
                    key={`${category}-header`} 
                    disabled 
                    sx={{ 
                      opacity: 0.7, 
                      fontWeight: 700, 
                      fontSize: '0.75rem',
                      color: BRAND_COLORS.text.secondary,
                      bgcolor: '#F8FAFC',
                      borderBottom: '1px solid #E5E7EB',
                      '&.Mui-disabled': {
                        opacity: 0.7,
                      },
                    }}
                  >
                    {category}
                  </MenuItem>,
                  ...units.map((unit) => (
                    <MenuItem 
                      key={unit.value} 
                      value={unit.value}
                      sx={{ 
                        fontWeight: 500,
                        pl: 4,
                        '&:hover': {
                          bgcolor: alpha(BRAND_COLORS.primary, 0.04),
                        },
                        '&.Mui-selected': {
                          bgcolor: alpha(BRAND_COLORS.primary, 0.08),
                          '&:hover': {
                            bgcolor: alpha(BRAND_COLORS.primary, 0.12),
                          },
                        },
                      }}
                    >
                      {unit.label}
                    </MenuItem>
                  )),
                ])}
              </StyledSelect>
              {touched?.unit_of_measure && errors.unit_of_measure && (
                <FormHelperText>{errors.unit_of_measure}</FormHelperText>
              )}
            </StyledFormControl>
          </Grid>

          <Grid item xs={12} md={4}>
            <StyledTextField
              fullWidth
              label="Price per Unit"
              name="price"
              type="number"
              value={formData.price || ''}
              onChange={(e) => onChange('price', e.target.value)}
              onBlur={() => onBlur('price')}
              error={!!errors.price && touched?.price}
              helperText={touched?.price && errors.price}
              placeholder="Enter price"
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Typography fontWeight={700} color="textSecondary">₦</Typography>
                  </InputAdornment>
                ),
                endAdornment: formData.unit_of_measure ? (
                  <InputAdornment position="end">
                    <Typography fontWeight={700} color="textSecondary" sx={{ fontSize: '0.8rem' }}>
                      /{getUnitLabel(formData.unit_of_measure)}
                    </Typography>
                  </InputAdornment>
                ) : null,
              }}
            />
            {formData.unit_of_measure && formData.price && (
              <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5, display: 'block', fontWeight: 500 }}>
                Price: ₦{formData.price} per {getUnitLabel(formData.unit_of_measure)}
              </Typography>
            )}
          </Grid>
          <Grid item xs={12} md={4}>
            <StyledFormControl fullWidth error={!!errors.Currency && touched?.Currency} required>
              <InputLabel>Currency</InputLabel>
              <StyledSelect
                name="Currency"
                value={formData.Currency || ''}
                onChange={(e) => onChange('Currency', e.target.value)}
                onBlur={() => onBlur('Currency')}
                label="Currency"
                sx={{ minWidth: 150 }}
              >
                <MenuItem value="" disabled>Select currency</MenuItem>
                {CURRENCIES.map((curr) => (
                  <MenuItem key={curr} value={curr} sx={{ fontWeight: 500 }}>{curr}</MenuItem>
                ))}
              </StyledSelect>
              {touched?.Currency && errors.Currency && (
                <FormHelperText>{errors.Currency}</FormHelperText>
              )}
            </StyledFormControl>
          </Grid>
          <Grid item xs={12} md={4}>
            <StyledTextField
              fullWidth
              label="Discount %"
              name="Discounts"
              type="number"
              value={formData.Discounts || ''}
              onChange={(e) => onChange('Discounts', e.target.value)}
              placeholder="Enter discount if any"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Typography fontWeight={700} color="textSecondary">%</Typography>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          {discountedPrice !== null && (
            <Grid item xs={12}>
              <Zoom in={true} timeout={400}>
                <Alert 
                  severity="info"
                  icon={<CheckIcon />}
                  sx={{ 
                    borderRadius: 3,
                    bgcolor: alpha(BRAND_COLORS.info, 0.06),
                    border: `1px solid ${alpha(BRAND_COLORS.info, 0.15)}`,
                    '& .MuiAlert-icon': {
                      color: BRAND_COLORS.info,
                    },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap' }}>
                    <Typography variant="body2" fontWeight={500}>
                      Original Price: <Typography component="span" fontWeight={700} color="text.primary">
                        ₦{formData.price} / {getUnitLabel(formData.unit_of_measure || 'kg')}
                      </Typography>
                    </Typography>
                    <Chip 
                      label={`${formData.Discounts}% off`}
                      size="small"
                      sx={{ 
                        bgcolor: BRAND_COLORS.error,
                        color: 'white',
                        fontWeight: 700,
                        borderRadius: 6,
                        '& .MuiChip-label': {
                          px: 1.5,
                        },
                      }}
                    />
                    <Typography variant="body2" fontWeight={700} color={BRAND_COLORS.primary}>
                      Discounted Price: ₦{discountedPrice.toFixed(2)} / {getUnitLabel(formData.unit_of_measure || 'kg')}
                    </Typography>
                  </Box>
                </Alert>
              </Zoom>
            </Grid>
          )}
        </Grid>
      </FormSection>
    </Box>
  );
});

// Step 3: Environment & Safety
const EnvironmentSafetyStep = React.memo(({ formData, onChange, errors, onBlur, touched }) => {
  return (
    <Box>
      <FormSection>
        <SectionTitle variant="h6">
          <SecurityIcon />
          Environmental & Safety Information
        </SectionTitle>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <StyledTextField
              fullWidth
              label="Environmental Impact"
              name="EnvironmentalImpact"
              value={formData.EnvironmentalImpact || ''}
              onChange={(e) => onChange('EnvironmentalImpact', e.target.value)}
              onBlur={() => onBlur('EnvironmentalImpact')}
              error={!!errors.EnvironmentalImpact && touched?.EnvironmentalImpact}
              helperText={touched?.EnvironmentalImpact && errors.EnvironmentalImpact}
              placeholder="Describe environmental impact"
              required
              multiline
              rows={3}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <StyledTextField
              fullWidth
              label="Lifecycle & Disposal"
              name="LifecycleDisposal"
              value={formData.LifecycleDisposal || ''}
              onChange={(e) => onChange('LifecycleDisposal', e.target.value)}
              onBlur={() => onBlur('LifecycleDisposal')}
              error={!!errors.LifecycleDisposal && touched?.LifecycleDisposal}
              helperText={touched?.LifecycleDisposal && errors.LifecycleDisposal}
              placeholder="Enter lifecycle and disposal advice"
              required
              multiline
              rows={3}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <StyledTextField
              fullWidth
              label="MSDS (Safety Data Sheet)"
              name="MSDS"
              value={formData.MSDS || ''}
              onChange={(e) => onChange('MSDS', e.target.value)}
              onBlur={() => onBlur('MSDS')}
              error={!!errors.MSDS && touched?.MSDS}
              helperText={touched?.MSDS && errors.MSDS}
              placeholder="Enter handling, storage, and safety advice"
              required
              multiline
              rows={3}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <StyledTextField
              fullWidth
              label="Health Risks"
              name="HealthRisks"
              value={formData.HealthRisks || ''}
              onChange={(e) => onChange('HealthRisks', e.target.value)}
              onBlur={() => onBlur('HealthRisks')}
              error={!!errors.HealthRisks && touched?.HealthRisks}
              helperText={touched?.HealthRisks && errors.HealthRisks}
              placeholder="Enter potential hazards to human health"
              required
              multiline
              rows={3}
            />
          </Grid>
        </Grid>
      </FormSection>

      <Alert 
        severity="warning"
        icon={<WarningIcon />}
        sx={{ 
          borderRadius: 3,
          bgcolor: alpha(BRAND_COLORS.warning, 0.06),
          border: `1px solid ${alpha(BRAND_COLORS.warning, 0.15)}`,
          '& .MuiAlert-icon': {
            color: BRAND_COLORS.warning,
          },
        }}
      >
        <Typography variant="body2" fontWeight={600}>
          Please ensure all safety and environmental information is accurate and complete.
          This information is critical for regulatory compliance and safe handling.
        </Typography>
      </Alert>
    </Box>
  );
});

// ============================================
// MAIN PRODUCT REGISTRATION COMPONENT
// ============================================

const ProductRegistration = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // ============================================
  // LOCAL STORAGE DATA
  // ============================================
  
  const userData = useMemo(() => {
    try {
      const data = localStorage.getItem('user_data');
      return data ? JSON.parse(data) : null;
    } catch (e) {
      console.debug('Error parsing user_data:', e);
      return null;
    }
  }, []);

  const userType = localStorage.getItem('user_type') || 'supplier';

  // ============================================
  // STATE
  // ============================================

  const [loading, setLoading] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(!isMobile);
  const [selectedTab, setSelectedTab] = useState('registration');
  const [activeStep, setActiveStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [touched, setTouched] = useState({});
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [registeredProduct, setRegisteredProduct] = useState(null);
  const [notifAnchorEl, setNotifAnchorEl] = useState(null);
  const [profileAnchorEl, setProfileAnchorEl] = useState(null);
  const [notifications] = useState([]);
  
  const [formData, setFormData] = useState({
    CASNumber: '',
    chemicalName: '',
    description: '',
    applications: '',
    productType: '',
    hazardClassifications: '',
    Synonyms: '',
    Formula: '',
    Grade: '',
    otherGrade: '',
    image: null,
    imageError: '',
    ManufacturerName: '',
    Country_ofOrigin: 'Nigeria',
    Certifications: '',
    DeliveryTime: '',
    unit_of_measure: '',
    price: '',
    Currency: 'NGN',
    Discounts: '',
    hs_code: '', // ✅ NEW
    EnvironmentalImpact: '',
    LifecycleDisposal: '',
    MSDS: '',
    HealthRisks: '',
    status: 'inactive',
  });
  const [errors, setErrors] = useState({});

  // ============================================
  // STEP DEFINITIONS
  // ============================================

  const steps = useMemo(() => [
    {
      label: 'Product Info',
      description: 'Basic product details & image',
      component: ProductInfoStep,
    },
    {
      label: 'Supply Chain',
      description: 'Manufacturers & pricing',
      component: ManufacturerSupplierStep,
    },
    {
      label: 'Safety & Compliance',
      description: 'Environment & safety',
      component: EnvironmentSafetyStep,
    },
  ], []);

  const menuItems = useMemo(() => [
    { id: 'dashboard', label: 'Dashboard', icon: <DashboardIcon /> },
    { id: 'products', label: 'Products', icon: <InventoryIcon /> },
    { id: 'orders', label: 'Orders', icon: <ShoppingCartIcon /> },
    { id: 'registration', label: 'Register Product', icon: <VerifiedIcon /> },
    { id: 'analytics', label: 'Analytics', icon: <AnalyticsIcon /> },
    { id: 'settings', label: 'Settings', icon: <SettingsIcon /> },
  ], []);

  // ============================================
  // AUTH CHECK
  // ============================================

  useEffect(() => {
    const token = localStorage.getItem('access_token');
    if (!token || userType !== 'supplier') {
      console.debug('Unauthorized access, redirecting to auth');
      navigate('/RawAuth');
      return;
    }
    const timer = setTimeout(() => {
      setLoading(false);
      console.debug('Product registration loaded successfully');
    }, 500);
    return () => clearTimeout(timer);
  }, [navigate, userType]);

  // ============================================
  // VALIDATION
  // ============================================

  const validateStep = useCallback((step) => {
    const newErrors = {};
    const newTouched = {};
    console.debug(`Validating step ${step}`);

    switch (step) {
      case 0:
        if (!formData.chemicalName?.trim()) {
          newErrors.chemicalName = 'Chemical Name is required';
          newTouched.chemicalName = true;
        }
        if (!formData.CASNumber?.trim()) {
          newErrors.CASNumber = 'CAS Number is required';
          newTouched.CASNumber = true;
        }
        if (!formData.description?.trim()) {
          newErrors.description = 'Description is required';
          newTouched.description = true;
        }
        if (!formData.applications) {
          newErrors.applications = 'Applications is required';
          newTouched.applications = true;
        }
        if (!formData.productType) {
          newErrors.productType = 'Product Type is required';
          newTouched.productType = true;
        }
        if (!formData.hazardClassifications) {
          newErrors.hazardClassifications = 'Hazard Classification is required';
          newTouched.hazardClassifications = true;
        }
        if (!formData.Formula?.trim()) {
          newErrors.Formula = 'Chemical Formula is required';
          newTouched.Formula = true;
        }
        if (!formData.Grade) {
          newErrors.Grade = 'Grade is required';
          newTouched.Grade = true;
        }
        if (formData.Grade === 'Others' && !formData.otherGrade?.trim()) {
          newErrors.otherGrade = 'Please specify the grade';
          newTouched.otherGrade = true;
        }
        if (!formData.image) {
          newErrors.image = 'Product image is required';
        }
        break;

      case 1:
        if (!formData.ManufacturerName?.trim()) {
          newErrors.ManufacturerName = 'Manufacturer Name is required';
          newTouched.ManufacturerName = true;
        }
        if (!formData.Country_ofOrigin?.trim()) {
          newErrors.Country_ofOrigin = 'Country of Origin is required';
          newTouched.Country_ofOrigin = true;
        }
        if (!formData.Certifications) {
          newErrors.Certifications = 'Certifications is required';
          newTouched.Certifications = true;
        }
        if (!formData.DeliveryTime) {
          newErrors.DeliveryTime = 'Delivery Time is required';
          newTouched.DeliveryTime = true;
        }
        if (!formData.unit_of_measure) {
          newErrors.unit_of_measure = 'Unit of Measure is required';
          newTouched.unit_of_measure = true;
        }
        if (!formData.price || parseFloat(formData.price) <= 0) {
          newErrors.price = 'Valid price is required';
          newTouched.price = true;
        }
        if (!formData.Currency) {
          newErrors.Currency = 'Currency is required';
          newTouched.Currency = true;
        }
        // ✅ NEW: Optional HS Code format validation
        if (formData.hs_code && formData.hs_code.trim() !== '' && 
            !/^\d{4}(\.\d{2}){0,2}$/.test(formData.hs_code.trim())) {
          newErrors.hs_code = 'HS Code must be in format like 2807 or 2807.00';
          newTouched.hs_code = true;
        }
        break;

      case 2:
        if (!formData.EnvironmentalImpact?.trim()) {
          newErrors.EnvironmentalImpact = 'Environmental Impact is required';
          newTouched.EnvironmentalImpact = true;
        }
        if (!formData.LifecycleDisposal?.trim()) {
          newErrors.LifecycleDisposal = 'Lifecycle & Disposal is required';
          newTouched.LifecycleDisposal = true;
        }
        if (!formData.MSDS?.trim()) {
          newErrors.MSDS = 'MSDS information is required';
          newTouched.MSDS = true;
        }
        if (!formData.HealthRisks?.trim()) {
          newErrors.HealthRisks = 'Health Risks is required';
          newTouched.HealthRisks = true;
        }
        break;

      default:
        break;
    }

    setErrors(newErrors);
    setTouched(prev => ({ ...prev, ...newTouched }));
    
    const isValid = Object.keys(newErrors).length === 0;
    console.debug(`Step ${step} validation ${isValid ? 'passed' : 'failed'}`, newErrors);
    return isValid;
  }, [formData]);

  // ============================================
  // HANDLERS
  // ============================================

  const handleLogout = useCallback(() => {
    console.debug('Logging out user');
    localStorage.clear();
    navigate('/RawAuth');
    setSnackbar({ open: true, message: 'Logged out successfully', severity: 'info' });
  }, [navigate]);

  const handleChangePassword = useCallback(() => {
    setProfileAnchorEl(null);
    setSnackbar({ open: true, message: 'Password change dialog would open', severity: 'info' });
    console.debug('Change password requested');
  }, []);

  const handleChange = useCallback((field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
    console.debug(`Field ${field} updated to:`, value);
  }, [errors]);

  const handleBlur = useCallback((field) => {
    setTouched(prev => ({ ...prev, [field]: true }));
    if (formData[field] || field === 'image') {
      validateStep(activeStep);
    }
    console.debug(`Field ${field} blurred`);
  }, [formData, activeStep, validateStep]);

  const handleNext = useCallback(() => {
    if (validateStep(activeStep)) {
      setActiveStep((prev) => Math.min(prev + 1, steps.length - 1));
      window.scrollTo({ top: 0, behavior: 'smooth' });
      console.debug(`Moving to step ${activeStep + 1}`);
    }
  }, [activeStep, validateStep, steps.length]);

  const handleBack = useCallback(() => {
    setActiveStep((prev) => Math.max(prev - 1, 0));
    window.scrollTo({ top: 0, behavior: 'smooth' });
    console.debug(`Moving back to step ${activeStep - 1}`);
  }, [activeStep]);

  const handleSubmit = useCallback(async () => {
    console.debug('Starting product submission');
    
    if (!validateStep(2)) {
      console.debug('Validation failed for step 2');
      return;
    }

    setSubmitting(true);

    try {
      const formDataToSend = new FormData();
      
      let userId = null;
      
      try {
        const userDataStr = localStorage.getItem('user_data');
        if (userDataStr) {
          const userData = JSON.parse(userDataStr);
          userId = userData.id;
          console.debug('✅ User ID from user_data:', userId);
        }
      } catch (e) {
        console.debug('Error parsing user_data:', e);
      }
      
      if (!userId) {
        userId = localStorage.getItem('user_id');
        console.debug('✅ User ID from user_id key:', userId);
      }
      
      if (!userId) {
        try {
          const userDataStr = localStorage.getItem('user_data');
          if (userDataStr) {
            const userData = JSON.parse(userDataStr);
            userId = userData.id || userData.user_id || userData.ID || userData.UserId;
            console.debug('✅ User ID from alternate fields:', userId);
          }
        } catch (e) {
          console.debug('Error parsing user_data (alternate):', e);
        }
      }
      
      if (userId) {
        formDataToSend.append('supplier_id', userId);
        console.debug('✅ Added supplier_id to form data:', userId);
      } else {
        console.error('❌ No user ID found in localStorage');
        throw new Error('User not authenticated. Please login again.');
      }
      
      const fieldMapping = {
        chemicalName: 'chemicalName',
        CASNumber: 'CASNumber',
        description: 'description',
        applications: 'applications',
        productType: 'productType',
        hazardClassifications: 'hazardClassifications',
        Synonyms: 'Synonyms',
        Formula: 'Formula',
        Grade: 'Grade',
        otherGrade: 'otherGrade',
        ManufacturerName: 'ManufacturerName',
        Country_ofOrigin: 'Country_ofOrigin',
        Certifications: 'Certifications',
        DeliveryTime: 'DeliveryTime',
        price: 'price',
        Currency: 'Currency',
        Discounts: 'Discounts',
        unit_of_measure: 'unit_of_measure',
        hs_code: 'hs_code', // ✅ NEW
        EnvironmentalImpact: 'EnvironmentalImpact',
        LifecycleDisposal: 'LifecycleDisposal',
        MSDS: 'MSDS',
        HealthRisks: 'HealthRisks',
        status: 'status',
      };
      
      Object.keys(fieldMapping).forEach(key => {
        const value = formData[key];
        if (value !== null && value !== undefined && value !== '') {
          const dbFieldName = fieldMapping[key];
          if (typeof value === 'string') {
            const sanitized = value.replace(/[<>]/g, '');
            formDataToSend.append(dbFieldName, sanitized);
          } else {
            formDataToSend.append(dbFieldName, value);
          }
        }
      });

      if (formData.image) {
        if (formData.image instanceof File) {
          formDataToSend.append('image', formData.image);
        } else if (typeof formData.image === 'string' && formData.image.startsWith('data:image')) {
          const response = await fetch(formData.image);
          const blob = await response.blob();
          const file = new File([blob], 'product-image.png', { type: blob.type });
          formDataToSend.append('image', file);
        }
      }

      console.debug('Sending request to:', API_ENDPOINTS.PRODUCTS.REGISTER);
      console.debug('Form data keys:', [...formDataToSend.keys()]);

      const response = await fetch(API_ENDPOINTS.PRODUCTS.REGISTER, {
        method: 'POST',
        body: formDataToSend,
      });

      const responseText = await response.text();
      console.debug('Server response:', responseText);

      let result;
      try {
        result = JSON.parse(responseText);
      } catch (parseError) {
        console.error('Error parsing server response:', parseError);
        throw new Error('Invalid response from server');
      }

      if (result.success) {
        setRegisteredProduct(result.data);
        setShowSuccessDialog(true);
        setSnackbar({
          open: true,
          message: 'Product registered successfully!',
          severity: 'success',
        });
        console.debug('Product registered successfully:', result.data);
      } else {
        throw new Error(result.message || 'Failed to register product');
      }
    } catch (error) {
      console.error('Submission Error:', error);
      setSnackbar({
        open: true,
        message: error.message || 'An error occurred while submitting the form',
        severity: 'error',
      });
    } finally {
      setSubmitting(false);
    }
  }, [formData, validateStep]);

  const handleReset = useCallback(() => {
    console.debug('Resetting form');
    setFormData({
      CASNumber: '',
      chemicalName: '',
      description: '',
      applications: '',
      productType: '',
      hazardClassifications: '',
      Synonyms: '',
      Formula: '',
      Grade: '',
      otherGrade: '',
      image: null,
      imageError: '',
      ManufacturerName: '',
      Country_ofOrigin: '',
      Certifications: '',
      DeliveryTime: '',
      unit_of_measure: '',
      price: '',
      Currency: '',
      Discounts: '',
      hs_code: '', // ✅ NEW
      EnvironmentalImpact: '',
      LifecycleDisposal: '',
      MSDS: '',
      HealthRisks: '',
      status: 'inactive',
    });
    setErrors({});
    setTouched({});
    setActiveStep(0);
    setShowSuccessDialog(false);
    setRegisteredProduct(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const handleTabChange = useCallback((tab) => {
    console.debug(`Navigating to tab: ${tab}`);
    if (tab === 'dashboard') {
      navigate('/supplier/dashboard');
    } else if (tab === 'products') {
      navigate('/supplier/products');
    } else if (tab === 'orders') {
      navigate('/supplier/orders');
    } else if (tab === 'settings') {
      navigate('/supplier/settings');
    } else if (tab === 'analytics') {
      navigate('/supplier/analytics');
    } else {
      setSelectedTab(tab);
    }
  }, [navigate]);

  // ============================================
  // RENDER STEP
  // ============================================

  const renderStep = useCallback(() => {
    const StepComponent = steps[activeStep].component;
    return (
      <Fade in={true} timeout={300}>
        <Box>
          <StepComponent
            formData={formData}
            onChange={handleChange}
            errors={errors}
            onBlur={handleBlur}
            touched={touched}
          />
        </Box>
      </Fade>
    );
  }, [activeStep, formData, errors, touched, handleChange, handleBlur, steps]);

  // ============================================
  // RENDER
  // ============================================

  if (loading) {
    return <Preloader text="Loading Registration..." size={60} fullScreen />;
  }

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: '#F0F2F5' }}>
      <Sidebar
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        isMobile={isMobile}
        selectedTab={selectedTab}
        onTabChange={handleTabChange}
        userData={userData}
        onLogout={handleLogout}
      />

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          width: { xs: '100%', md: `calc(100% - 280px)` },
        }}
      >
        <Header
          isMobile={isMobile}
          onDrawerToggle={() => setDrawerOpen(true)}
          selectedTab={selectedTab}
          menuItems={menuItems}
          searchTerm=""
          onSearchChange={() => {}}
          cartItemCount={0}
          onCartOpen={() => {}}
          notifications={notifications}
          userData={userData}
          onLogout={handleLogout}
          onChangePassword={handleChangePassword}
          onSettings={() => handleTabChange('settings')}
        />

        <Box sx={{ flex: 1, p: { xs: 2, sm: 3 }, overflow: 'auto' }}>
          <Container maxWidth="lg" sx={{ px: { xs: 0, sm: 0 } }}>
            <Box sx={{ mb: 4 }}>
              <Typography 
                variant="h4" 
                fontWeight={800} 
                sx={{ 
                  fontSize: { xs: '1.5rem', sm: '2rem' },
                  color: BRAND_COLORS.text.primary,
                  mb: 0.5,
                }}
              >
                Register New Product
              </Typography>
              <Typography 
                variant="body1" 
                color="textSecondary"
                fontWeight={500}
                sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}
              >
                Fill in the details below to register a new chemical product
              </Typography>
            </Box>

            <StyledCard>
              <Box sx={{ 
                px: { xs: 2, sm: 3, md: 4 }, 
                py: { xs: 2, sm: 3 }, 
                borderBottom: '1px solid #E5E7EB',
                bgcolor: '#FAFBFC',
              }}>
                <Stepper
                  activeStep={activeStep}
                  orientation={isMobile ? 'vertical' : 'horizontal'}
                  connector={<CustomStepConnector />}
                  sx={{ py: 1 }}
                >
                  {steps.map((step, index) => (
                    <Step key={step.label}>
                      <StepLabel
                        StepIconComponent={(props) => (
                          <CustomStepIcon {...props} icon={index + 1} />
                        )}
                        onClick={() => {
                          if (index < activeStep) {
                            setActiveStep(index);
                            console.debug(`Jumped to step ${index}`);
                          }
                        }}
                        sx={{ 
                          cursor: index < activeStep ? 'pointer' : 'default',
                          '& .MuiStepLabel-label': {
                            fontWeight: activeStep === index ? 700 : 500,
                          },
                        }}
                      >
                        <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                          <Typography 
                            variant="body2" 
                            fontWeight={activeStep === index ? 700 : 600}
                            color={activeStep === index ? BRAND_COLORS.primary : 'textSecondary'}
                            sx={{ fontSize: '0.9rem' }}
                          >
                            {step.label}
                          </Typography>
                          <Typography 
                            variant="caption" 
                            color="textSecondary"
                            sx={{ display: { xs: 'none', md: 'block' }, fontWeight: 500 }}
                          >
                            {step.description}
                          </Typography>
                        </Box>
                      </StepLabel>
                    </Step>
                  ))}
                </Stepper>
              </Box>

              <Box sx={{ px: { xs: 2, sm: 3, md: 4 }, py: { xs: 3, sm: 4 } }}>
                {renderStep()}
              </Box>

              <Box
                sx={{
                  px: { xs: 2, sm: 3, md: 4 },
                  py: { xs: 2, sm: 3 },
                  borderTop: '1px solid #E5E7EB',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  bgcolor: '#FAFBFC',
                  flexWrap: 'wrap',
                  gap: 2,
                }}
              >
                <NavButton
                  variant="outlined"
                  onClick={handleBack}
                  disabled={activeStep === 0}
                  startIcon={<ArrowBackIcon />}
                  sx={{
                    borderColor: '#D1D5DB',
                    color: BRAND_COLORS.text.primary,
                    '&:hover': {
                      borderColor: BRAND_COLORS.primary,
                      bgcolor: alpha(BRAND_COLORS.primary, 0.04),
                      color: BRAND_COLORS.primary,
                    },
                    '&:disabled': {
                      opacity: 0.4,
                    },
                  }}
                >
                  Back
                </NavButton>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  {activeStep === steps.length - 1 ? (
                    <NavButton
                      variant="contained"
                      onClick={handleSubmit}
                      disabled={submitting}
                      startIcon={submitting ? (
                        <LinearProgress 
                          size={20} 
                          sx={{ 
                            width: 20, 
                            color: 'white',
                            '& .MuiLinearProgress-bar': {
                              bgcolor: 'white',
                            },
                          }} 
                        />
                      ) : <CheckIcon />}
                      sx={{
                        bgcolor: BRAND_COLORS.success,
                        color: 'white',
                        '&:hover': {
                          bgcolor: BRAND_COLORS.success,
                          boxShadow: `0 8px 30px ${alpha(BRAND_COLORS.success, 0.3)}`,
                        },
                        '&:disabled': {
                          bgcolor: '#D1D5DB',
                        },
                      }}
                    >
                      {submitting ? 'Submitting...' : 'Submit Product'}
                    </NavButton>
                  ) : (
                    <NavButton
                      variant="contained"
                      onClick={handleNext}
                      endIcon={<ArrowForwardIcon />}
                      sx={{
                        bgcolor: BRAND_COLORS.primary,
                        color: 'white',
                        '&:hover': {
                          bgcolor: BRAND_COLORS.primaryDark,
                          boxShadow: `0 8px 30px ${alpha(BRAND_COLORS.primary, 0.3)}`,
                        },
                      }}
                    >
                      Next
                    </NavButton>
                  )}
                </Box>
              </Box>
            </StyledCard>
          </Container>
        </Box>

        <Footer />
      </Box>

      <Popover
        open={Boolean(notifAnchorEl)}
        anchorEl={notifAnchorEl}
        onClose={() => setNotifAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            width: 340,
            maxHeight: 400,
            borderRadius: 16,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
            border: '1px solid #E5E7EB',
          },
        }}
      >
        <Box sx={{ p: 2.5, borderBottom: '1px solid #E5E7EB' }}>
          <Typography variant="subtitle1" fontWeight={700}>
            Notifications
          </Typography>
        </Box>
        <Box sx={{ overflow: 'auto', p: 2.5 }}>
          <Typography variant="body2" color="textSecondary" fontWeight={500} textAlign="center">
            No notifications
          </Typography>
        </Box>
      </Popover>

      <Menu
        anchorEl={profileAnchorEl}
        open={Boolean(profileAnchorEl)}
        onClose={() => setProfileAnchorEl(null)}
        PaperProps={{
          sx: {
            width: 220,
            borderRadius: 16,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
            border: '1px solid #E5E7EB',
            mt: 1,
          },
        }}
      >
        <MenuItem 
          onClick={() => { handleTabChange('settings'); setProfileAnchorEl(null); }}
          sx={{ 
            fontWeight: 500,
            borderRadius: 1,
            '&:hover': { bgcolor: alpha(BRAND_COLORS.primary, 0.04) },
          }}
        >
          <PersonIcon fontSize="small" sx={{ mr: 1.5, color: BRAND_COLORS.primary }} />
          Profile
        </MenuItem>
        <MenuItem 
          onClick={handleChangePassword}
          sx={{ 
            fontWeight: 500,
            borderRadius: 1,
            '&:hover': { bgcolor: alpha(BRAND_COLORS.primary, 0.04) },
          }}
        >
          <LockOutlinedIcon fontSize="small" sx={{ mr: 1.5, color: BRAND_COLORS.primary }} />
          Change Password
        </MenuItem>
        <Divider sx={{ my: 1 }} />
        <MenuItem 
          onClick={handleLogout} 
          sx={{ 
            color: BRAND_COLORS.error,
            fontWeight: 600,
            borderRadius: 1,
            '&:hover': { bgcolor: alpha(BRAND_COLORS.error, 0.04) },
          }}
        >
          <LogoutIcon fontSize="small" sx={{ mr: 1.5, color: BRAND_COLORS.error }} />
          Logout
        </MenuItem>
      </Menu>

      <Dialog
        open={showSuccessDialog}
        onClose={() => setShowSuccessDialog(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 20,
            p: { xs: 2, sm: 3 },
            textAlign: 'center',
            boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
          },
        }}
      >
        <DialogTitle sx={{ p: 0, pt: 3 }}>
          <Box
            sx={{
              width: 88,
              height: 88,
              borderRadius: '50%',
              bgcolor: alpha(BRAND_COLORS.success, 0.08),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 2.5,
              animation: 'bounceIn 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
            }}
          >
            <CheckIcon sx={{ fontSize: 48, color: BRAND_COLORS.success }} />
          </Box>
          <Typography variant="h5" fontWeight={800} color={BRAND_COLORS.success}>
            Registration Successful!
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ p: 0, pt: 2.5 }}>
          <Typography variant="body1" color="textSecondary" fontWeight={500} gutterBottom>
            Your product has been registered successfully in the system.
          </Typography>
          {registeredProduct && (
            <Box
              sx={{
                mt: 3,
                p: 3,
                bgcolor: '#F8FAFC',
                borderRadius: 3,
                textAlign: 'left',
                border: '1px solid #E5E7EB',
              }}
            >
              <Typography variant="subtitle2" fontWeight={700} color="textSecondary" gutterBottom>
                Product Details
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
                <Typography variant="body2" color="textSecondary" fontWeight={500}>
                  Name:
                </Typography>
                <Typography variant="body2" fontWeight={700}>
                  {registeredProduct.name || formData.chemicalName}
                </Typography>
                <Typography variant="body2" color="textSecondary" fontWeight={500}>
                  CAS Number:
                </Typography>
                <Typography variant="body2" fontWeight={700}>
                  {registeredProduct.registryNumber || formData.CASNumber}
                </Typography>
                <Typography variant="body2" color="textSecondary" fontWeight={500}>
                  HS Code:
                </Typography>
                <Typography variant="body2" fontWeight={700}>
                  {registeredProduct.hs_code || formData.hs_code || 'N/A'}
                </Typography>
                <Typography variant="body2" color="textSecondary" fontWeight={500}>
                  ID:
                </Typography>
                <Typography variant="body2" fontWeight={700}>
                  {registeredProduct.id || 'N/A'}
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ 
          justifyContent: 'center', 
          p: 0, 
          pt: 3,
          gap: 2,
          flexWrap: 'wrap',
        }}>
          <NavButton
            variant="contained"
            onClick={() => navigate('/supplier/product')}
            sx={{
              bgcolor: BRAND_COLORS.primary,
              '&:hover': { bgcolor: BRAND_COLORS.primaryDark },
            }}
          >
            View Products
          </NavButton>
          <NavButton
            variant="outlined"
            onClick={handleReset}
            sx={{
              borderColor: '#D1D5DB',
              color: BRAND_COLORS.text.primary,
              '&:hover': {
                borderColor: BRAND_COLORS.primary,
                bgcolor: alpha(BRAND_COLORS.primary, 0.04),
                color: BRAND_COLORS.primary,
              },
            }}
          >
            Register Another
          </NavButton>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        TransitionComponent={Slide}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          icon={snackbar.severity === 'success' ? <CheckIcon /> : <WarningIcon />}
          sx={{
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
            fontWeight: 600,
            '& .MuiAlert-message': {
              fontWeight: 600,
            },
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      <style>
        {`
          @keyframes bounceIn {
            0% {
              transform: scale(0.3);
              opacity: 0;
            }
            50% {
              transform: scale(1.05);
              opacity: 1;
            }
            70% {
              transform: scale(0.9);
            }
            100% {
              transform: scale(1);
            }
          }
        `}
      </style>
    </Box>
  );
};

export default ProductRegistration;